import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import { useGoogleLogin } from "@react-oauth/google";
import { ColorfulLogo } from "@/components/ColorfulLogo";
import { useAuth } from "@/lib/auth";
import { TermsModal } from "@/components/TermsModal";
import { PrivacyModal } from "@/components/PrivacyModal";
import { Seo } from "@/lib/seo";

const HAS_GOOGLE = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden>
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden>
      <path fill="#F25022" d="M1 1h10v10H1z"/>
      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
      <path fill="#FFB900" d="M13 13h10v10H13z"/>
    </svg>
  );
}

function GoogleLoginButton({ setUser, navigate }: { setUser: (u: any) => void; navigate: (path: string) => void }) {
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const data = await res.json();
        setUser({ name: data.name, email: data.email, picture: data.picture });
        navigate("/");
      } catch {
        console.error("Failed to fetch user info");
      }
    },
    onError: () => console.error("Google login failed"),
  });

  return (
    <button
      onClick={() => googleLogin()}
      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all font-medium text-[14px] text-gray-700 shadow-sm hover:shadow active:scale-[0.98]"
    >
      <GoogleIcon />
      <span className="flex-1 text-center">Continue with Google</span>
    </button>
  );
}

export default function SignIn() {
  const { user, setUser } = useAuth();
  const [, navigate] = useLocation();
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <Seo
        title="Sign In | AgentsToGo"
        description="Sign in to your AgentsToGo account."
        path="/signin"
        noindex
      />
      {/* Background subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-sky-50/60 via-white to-indigo-50/40 pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative z-10 w-full max-w-sm"
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <ColorfulLogo size="lg" className="font-bold" />
          <p className="mt-2 text-sm text-gray-400 font-light">the one-stop shop to build all your agents</p>
        </div>

        {/* Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-xl shadow-gray-100/80 p-8">
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Welcome back</h1>
            <p className="text-sm text-gray-400 mt-1">Sign in to continue building your agents</p>
          </div>

          <div className="flex flex-col gap-3">
            {/* Google */}
            {HAS_GOOGLE ? (
              <GoogleLoginButton setUser={setUser} navigate={navigate} />
            ) : (
              <button
                disabled
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 opacity-40 cursor-not-allowed font-medium text-[14px] text-gray-700"
                title="Add VITE_GOOGLE_CLIENT_ID to enable"
              >
                <GoogleIcon />
                <span className="flex-1 text-center">Continue with Google</span>
              </button>
            )}

            {/* Apple */}
            <button
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-black hover:bg-gray-900 transition-all font-medium text-[14px] text-white shadow-sm hover:shadow-md active:scale-[0.98]"
              onClick={() => alert("Apple Sign-In requires Apple Developer credentials — coming soon.")}
            >
              <AppleIcon />
              <span className="flex-1 text-center">Continue with Apple</span>
            </button>

            {/* Microsoft */}
            <button
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all font-medium text-[14px] text-gray-700 shadow-sm hover:shadow active:scale-[0.98]"
              onClick={() => alert("Microsoft Sign-In coming soon.")}
            >
              <MicrosoftIcon />
              <span className="flex-1 text-center">Continue with Microsoft</span>
            </button>
          </div>

          {/* Divider */}
          <div className="mt-6 pt-5 border-t border-gray-100 text-center">
            <p className="text-[12px] text-gray-400 leading-relaxed">
              By signing in you agree to our{" "}
              <button onClick={() => setShowTerms(true)} className="underline hover:text-gray-600">Terms</button> and{" "}
              <button onClick={() => setShowPrivacy(true)} className="underline hover:text-gray-600">Privacy Policy</button>.
              <br />No password required.
            </p>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            ← Back to AgentsToGo
          </button>
        </div>
      </motion.div>

      <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyModal open={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
}
